// Tolerant reader: accepts canonical subset AND browser/execCommand artifacts
// (b/i/strike/del/div/styled spans/br), plus legacy plain text. Emits the IR.
import { isAllowedLinkUrl } from './linkSchemes';

const INLINE_TAG_MARKS = {
    STRONG: 'bold', B: 'bold',
    EM: 'italic', I: 'italic',
    U: 'underline',
    S: 'strike', STRIKE: 'strike', DEL: 'strike',
};
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IMG', 'VIDEO', 'IFRAME', 'OBJECT', 'EMBED']);

// Render-level use of the shared allowlist (src/lib/noteHtml/linkSchemes.js)
// for <a href>. This is the single choke point every consumer of note text
// shares (editor canonicalization, canvas painting, search, ops), so
// filtering here closes the stored-payload path even for hrefs a
// collaborator's client wrote directly into the Y.Doc (i.e. bypassing
// EditableNote's own execLink-time check).
const isAllowedLinkHref = isAllowedLinkUrl;

const marksFromSpanStyle = (el) => {
    const style = el.getAttribute && (el.getAttribute('style') || '');
    const marks = {};
    if (/font-weight\s*:\s*(bold|[6-9]00)/i.test(style)) marks.bold = true;
    if (/font-style\s*:\s*italic/i.test(style)) marks.italic = true;
    if (/text-decoration[^;]*underline/i.test(style)) marks.underline = true;
    if (/text-decoration[^;]*line-through/i.test(style)) marks.strike = true;
    return marks;
};

const pushRun = (runs, text, marks) => {
    if (!text) return;
    runs.push({ text, ...marks });
};

// Walks inline content of one block element into runs; <br> starts a new block.
// Blocks created by a <br> are flagged `brSplit`; blockFromElement drops the
// trailing one if it never received any text (the Chrome/contenteditable
// blank-line placeholder "<p><br></p>" and a trailing "text<br>" artifact),
// while a genuinely empty block BETWEEN two <br>s (or a leading one) is real
// content and is left alone.
const walkInline = (node, marks, blocks, current) => {
    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === 3) { pushRun(current.runs, child.nodeValue, marks); continue; }
        if (child.nodeType !== 1) continue;
        const tag = child.tagName;
        if (DROP_TAGS.has(tag)) continue;
        if (tag === 'BR') {
            current = { type: current.type, runs: [], brSplit: true };
            blocks.push(current);
            continue;
        }
        let childMarks = marks;
        if (INLINE_TAG_MARKS[tag]) childMarks = { ...marks, [INLINE_TAG_MARKS[tag]]: true };
        else if (tag === 'A' && child.getAttribute('href') && isAllowedLinkHref(child.getAttribute('href'))) childMarks = { ...marks, href: child.getAttribute('href') };
        else if (tag === 'SPAN') childMarks = { ...marks, ...marksFromSpanStyle(child) };
        current = walkInline(child, childMarks, blocks, current);
    }
    return current;
};

export const MARK_KEYS = ['bold', 'italic', 'underline', 'strike'];

// Merges adjacent runs with identical marks (and href) into one; guarantees
// at least one run comes out (an empty-text placeholder) even if none went
// in. Shared with htmlNoteOps.js, which re-merges runs after style toggles.
export const mergeRuns = (runs) => {
    const merged = [];
    for (const run of runs) {
        const prev = merged[merged.length - 1];
        const same = prev && MARK_KEYS.every((m) => !!prev[m] === !!run[m]) &&
            (prev.href || null) === (run.href || null);
        if (same) prev.text += run.text; else merged.push({ ...run });
    }
    if (!merged.length) merged.push({ text: '' });
    return merged;
};

const finishBlock = (block) => ({ type: block.type, runs: mergeRuns(block.runs) });

const blockFromElement = (el, type, blocks) => {
    const block = { type, runs: [] };
    blocks.push(block);
    const last = walkInline(el, {}, blocks, block);
    // Trailing-BR placeholder rule: only the block that ends THIS element and
    // was itself created by a <br> (never the element's own starting block)
    // gets dropped, and only if it stayed empty. An empty block earlier in
    // the same element (e.g. "a<br><br>b") is real content and survives.
    if (last.brSplit && last.runs.length === 0) blocks.pop();
};

export const parseNoteHtml = (text) => {
    const source = String(text ?? '');
    if (!source.trim()) return [{ type: 'paragraph', runs: [{ text: '' }] }];
    // Legacy plain-text notes (pre-rich-text) never start with a tag; a
    // stray "<" mid-string ("a < b") is common in plain prose and must NOT
    // flip this into the HTML branch (that would hand it to DOMParser, which
    // reads "< b" as literal text and collapses the "\n"-separated lines
    // into one paragraph). Checking only the string's start, not "any < at
    // all", is also what lets legitimate HTML that happens to embed a
    // caller-authored "<" work, and matches how a real HTML payload always
    // begins with its first tag.
    if (!source.trimStart().startsWith('<')) {
        return source.split('\n').map((line) => ({ type: 'paragraph', runs: [{ text: line }] }));
    }
    const doc = new DOMParser().parseFromString(source, 'text/html');
    const blocks = [];
    const visitTop = (node) => {
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType === 3) {
                if (child.nodeValue.trim()) blockFromElement({ childNodes: [child] }, 'paragraph', blocks);
                continue;
            }
            if (child.nodeType !== 1) continue;
            const tag = child.tagName;
            if (DROP_TAGS.has(tag)) continue;
            if (tag === 'P' || tag === 'DIV') blockFromElement(child, 'paragraph', blocks);
            else if (tag === 'UL' || tag === 'OL') {
                const type = tag === 'UL' ? 'bullet' : 'numbered';
                for (const li of Array.from(child.children)) {
                    if (li.tagName === 'LI') blockFromElement(li, type, blocks);
                }
            } else blockFromElement(child, 'paragraph', blocks); // unknown block-ish: unwrap as paragraph
        }
    };
    visitTop(doc.body);
    // Also the fallback for content with no blocks at all, e.g. an empty "<ul></ul>".
    if (!blocks.length) blocks.push({ type: 'paragraph', runs: [{ text: '' }] });
    return blocks.map(finishBlock);
};

export const plainTextFromNoteHtml = (text) =>
    parseNoteHtml(text).map((b) => b.runs.map((r) => r.text).join('')).join('\n');
